import React, { useEffect, useState } from "react";
import axios from "axios";
import "./SuperAdminPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("pos_token")}`,
  },
});

const parsePaymentBreakdown = (sale) => {
  try {
    if (typeof sale.payment_breakdown === "string") {
      return JSON.parse(sale.payment_breakdown || "{}");
    }

    return sale.payment_breakdown || sale.paymentBreakdown || {};
  } catch {
    return {};
  }
};


const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getSalePaymentTotals = (sale) => {
  const breakdown = parsePaymentBreakdown(sale);

  const saleTotal = toNumber(sale.total);

  const paymentMethod = String(
    sale.payment_method || sale.paymentMethod || "split"
  ).toLowerCase();

  const cashTendered = toNumber(
    breakdown.cash ??
      breakdown.cashPaid ??
      breakdown.cash_paid ??
      breakdown.cashTendered ??
      breakdown.cash_tendered ??
      sale.cash ??
      sale.cashPaid ??
      sale.cash_paid ??
      0
  );

  let cardTotal = toNumber(
    breakdown.card ??
      breakdown.cardPaid ??
      breakdown.card_paid ??
      sale.card ??
      sale.cardPaid ??
      sale.card_paid ??
      0
  );

  const change = toNumber(
    breakdown.change ??
      breakdown.changeDue ??
      breakdown.change_due ??
      breakdown.changeAmount ??
      breakdown.change_amount ??
      sale.change ??
      sale.changeDue ??
      sale.change_due ??
      sale.changeAmount ??
      sale.change_amount ??
      0
  );

  let cashTotal = 0;

  // Old sales with no payment breakdown
  if (cashTendered === 0 && cardTotal === 0) {
    if (paymentMethod === "cash") {
      cashTotal = saleTotal;
    } else if (paymentMethod === "card") {
      cardTotal = saleTotal;
    } else {
      cashTotal = saleTotal;
    }
  } else {
    // Card cannot be more than sale total
    cardTotal = Math.min(cardTotal, saleTotal);

    // If cash was used, cash sale amount is the remaining balance after card
    if (cashTendered > 0) {
      cashTotal = Math.max(0, saleTotal - cardTotal);
    } else {
      cashTotal = 0;
    }

    // Extra safety: if old data needs change subtraction
    if (cashTotal === 0 && cashTendered > 0 && change > 0) {
      cashTotal = Math.max(0, cashTendered - change);
    }
  }

  // Final safety: cash + card must never be bigger than sale total
  if (cashTotal + cardTotal > saleTotal) {
    cashTotal = Math.max(0, saleTotal - cardTotal);
  }

  return {
    saleTotal,
    cashTendered,
    cardTotal,
    change,
    cashTotal,
  };
};

const calculateSalesSummary = (sales = []) => {
  const activeSales = sales.filter((sale) => !sale.is_void);

  return activeSales.reduce(
    (summary, sale) => {
      const totals = getSalePaymentTotals(sale);

      summary.totalTransactions += 1;
      summary.totalSales += totals.saleTotal;
      summary.cashTotal += totals.cashTotal;
      summary.cardTotal += totals.cardTotal;

      return summary;
    },
    {
      totalTransactions: 0,
      totalSales: 0,
      cashTotal: 0,
      cardTotal: 0,
    }
  );
};

function SuperAdminSalesPage() {
  const [shops, setShops] = useState([]);
  const [selectedSalesShopId, setSelectedSalesShopId] = useState("");

  const [salesFrom, setSalesFrom] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [salesTo, setSalesTo] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const [shopSales, setShopSales] = useState([]);
  const [shopSalesSummary, setShopSalesSummary] = useState({
    totalTransactions: 0,
    totalSales: 0,
    cashTotal: 0,
    cardTotal: 0,
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadShops = async () => {
    try {
      const res = await axios.get(
  `${API_URL}/super-admin/shops`,
  getAuthConfig()
);
      setShops(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Load shops error:", err);
      setError(
  err.response?.data?.error ||
    err.message ||
    "Failed to load shops"
);
    }
  };

  const loadShopSales = async (e) => {
    if (e) e.preventDefault();

    try {
      setMessage("");
      setError("");

      if (!selectedSalesShopId) {
        setError("Please select a shop");
        return;
      }

     const res = await axios.get(
  `${API_URL}/super-admin/shops/${selectedSalesShopId}/sales`,
  {
    params: {
      from: salesFrom,
      to: salesTo,
    },
    ...getAuthConfig(),
  }
);

      const sales = res.data.sales || [];

      setShopSales(sales);

      // Do not use backend summary here because cash can include change.
      // Recalculate correctly on frontend.
      setShopSalesSummary(calculateSalesSummary(sales));
    } catch (err) {
      console.error("Load shop sales error:", err.response?.data || err.message);

      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load shop sales"
      );
    }
  };

  

  const voidSale = async (saleId) => {
    try {
      if (!selectedSalesShopId) {
        setError("Please select a shop first");
        return;
      }

      const reason = window.prompt(
        `Why do you want to void sale #${saleId}?`,
        "Mistake / test sale"
      );

      if (!reason) return;

      const confirmVoid = window.confirm(
        `Are you sure you want to void sale #${saleId}? This sale will stay in records but will not count in totals.`
      );

      if (!confirmVoid) return;

      setMessage("");
      setError("");

     await axios.put(
  `${API_URL}/super-admin/shops/${selectedSalesShopId}/sales/${saleId}/void`,
  {
    void_reason: reason,
    voided_by: "super_admin",
  },
  getAuthConfig()
);

      setMessage("Sale voided successfully");
      loadShopSales();
    } catch (err) {
      console.error("Void sale error:", err.response?.data || err.message);

      setError(
        err.response?.data?.error || err.message || "Failed to void sale"
      );
    }
  };

  useEffect(() => {
    loadShops();
  }, []);

  return (
    <div className="super-admin-page">
      <div className="super-admin-header">
        <div>
          <h1>Shop Sales</h1>
          <p>View sales for any shop and void incorrect sales.</p>
        </div>
      </div>

      {message && <div className="super-message">{message}</div>}
      {error && <div className="super-error">{error}</div>}

      <div className="super-card super-sales-card">
        <h2>View Shop Sales</h2>

        <form onSubmit={loadShopSales} className="super-form">
          <label>
            Select Shop
            <select
              value={selectedSalesShopId}
              onChange={(e) => setSelectedSalesShopId(e.target.value)}
            >
              <option value="">Select shop</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  #{shop.id} - {shop.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            From
            <input
              type="date"
              value={salesFrom}
              onChange={(e) => setSalesFrom(e.target.value)}
            />
          </label>

          <label>
            To
            <input
              type="date"
              value={salesTo}
              onChange={(e) => setSalesTo(e.target.value)}
            />
          </label>

          <button type="submit">Load Sales</button>
        </form>

        <div className="super-sales-summary">
          <div>
            <strong>
              €{Number(shopSalesSummary.totalSales || 0).toFixed(2)}
            </strong>
            <span>Total Sales</span>
          </div>

          <div>
            <strong>{shopSalesSummary.totalTransactions || 0}</strong>
            <span>Transactions</span>
          </div>

          <div>
            <strong>
              €{Number(shopSalesSummary.cashTotal || 0).toFixed(2)}
            </strong>
            <span>Cash</span>
          </div>

          <div>
            <strong>
              €{Number(shopSalesSummary.cardTotal || 0).toFixed(2)}
            </strong>
            <span>Card</span>
          </div>
        </div>

        <div className="super-sales-table-wrap">
          <table className="super-table">
            <thead>
              <tr>
                <th>Sale ID</th>
                <th>Date</th>
                <th>Total</th>
                <th>Cash</th>
                <th>Card</th>
                <th>Change</th>
                <th>Items</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {shopSales.length === 0 ? (
                <tr>
                  <td colSpan="9">No sales found</td>
                </tr>
              ) : (
                shopSales.map((sale) => {
                  let items = [];

                  try {
                    items =
                      typeof sale.items === "string"
                        ? JSON.parse(sale.items || "[]")
                        : sale.items || [];
                  } catch {
                    items = [];
                  }

                  const totals = getSalePaymentTotals(sale);

                  return (
                    <tr
                      key={sale.id}
                      className={sale.is_void ? "void-sale-row" : ""}
                    >
                      <td>#{sale.id}</td>
                      <td>{new Date(sale.created_at).toLocaleString()}</td>
                      <td>€{Number(sale.total || 0).toFixed(2)}</td>
                      <td>€{totals.cashTotal.toFixed(2)}</td>
                      <td>€{totals.cardTotal.toFixed(2)}</td>
                      <td>€{totals.change.toFixed(2)}</td>
                      <td>{items.length}</td>
                      <td>
                        {sale.is_void ? (
                          <span className="sale-status-void">VOID</span>
                        ) : (
                          <span className="sale-status-complete">COMPLETE</span>
                        )}
                      </td>
                      <td>
                        {sale.is_void ? (
                          <span className="void-reason-text">
                            {sale.void_reason || "Voided"}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="super-void-sale-btn"
                            onClick={() => voidSale(sale.id)}
                          >
                            Void
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminSalesPage;